import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { ChatCircle, Bug, Lightbulb, Sparkle, Star, X } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPES = [
  { value: 'bug', label: 'Bug rencontré', Icon: Bug, color: 'text-red-600 bg-red-50 border-red-200' },
  { value: 'suggestion', label: 'Suggestion', Icon: Lightbulb, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'feature', label: 'Idée de fonctionnalité', Icon: Sparkle, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { value: 'satisfaction', label: 'Satisfaction', Icon: Star, color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
];

export function FeedbackFloatingButton({ user }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [sending, setSending] = useState(false);

  // Visible uniquement pour les pilotes (et admins pour test)
  if (!user || (!user.is_pilot && user.role !== 'admin')) return null;

  const submit = async () => {
    if (!message.trim()) { toast.error('Veuillez écrire un message'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/feedback`, { type, message: message.trim(), rating: rating || null }, { withCredentials: true });
      toast.success('Merci pour votre retour !');
      setOpen(false); setMessage(''); setRating(0); setType('suggestion');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="fixed bottom-20 left-4 sm:bottom-6 sm:left-4 z-[9998] bg-gradient-to-r from-[#0055FF] to-[#FF6B00] text-white rounded-full shadow-lg hover:shadow-xl px-4 sm:px-5 py-3 flex items-center gap-2 transition-all hover:scale-105"
        data-testid="feedback-floating-btn">
        <ChatCircle size={20} weight="bold" />
        <span className="text-sm font-semibold hidden sm:inline">Donner mon avis</span>
        <span className="text-xs font-semibold sm:hidden">Avis</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ChatCircle size={22} weight="bold" className="text-[#0055FF]" /> Donner mon avis</DialogTitle>
            <DialogDescription>Votre retour est précieux pour améliorer CHANTIX. Merci !</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2">Type de retour</label>
              <div className="grid grid-cols-2 gap-2">
                {TYPES.map(t => (
                  <button key={t.value} onClick={() => setType(t.value)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 text-sm transition ${type === t.value ? t.color + ' border-opacity-100' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    data-testid={`feedback-type-${t.value}`}>
                    <t.Icon size={18} /> {t.label}
                  </button>
                ))}
              </div>
            </div>

            {type === 'satisfaction' && (
              <div>
                <label className="text-sm font-medium block mb-2">Note (1-5)</label>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => setRating(n)}
                      className={`p-2 transition ${rating >= n ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}>
                      <Star size={28} weight={rating >= n ? 'fill' : 'regular'} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium block mb-2">Votre message</label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder={type === 'bug' ? 'Décrivez le bug rencontré (où, quand, ce qui s\'est passé)...' : 'Votre message...'}
                rows={5} className="w-full" data-testid="feedback-message" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={sending || !message.trim()} className="bg-[#0055FF] hover:bg-blue-700" data-testid="submit-feedback-btn">
              {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Envoyer mon retour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
